import type { Activity } from '../types/activity';

/** Read every authorized page before exposing a dashboard snapshot. Never show partial totals. */
export async function loadActivitySnapshot(fetchPage: (url: string) => Promise<unknown>) {
  const activities: Activity[] = [];
  const ids = new Set<string>(), cursors = new Set<string>();
  let cursor: string | null = null, total: number | undefined, capabilities: unknown;
  for (let pageIndex = 0; pageIndex < 1000; pageIndex += 1) {
    const params = new URLSearchParams({ limit: '200' });
    if (cursor) params.set('cursor', cursor);
    const value = await fetchPage(`/api/v1/activities?${params}`);
    if (!value || typeof value !== 'object') throw new Error('Activity API returned an invalid snapshot');
    const page = value as { activities: Activity[]; nextCursor: string | null; total: number; capabilities?: unknown };
    if (!Array.isArray(page.activities) || !Number.isSafeInteger(page.total) || page.total < 0 || (page.nextCursor !== null && typeof page.nextCursor !== 'string')) throw new Error('Activity API returned an invalid page');
    if (total !== undefined && total !== page.total) throw new Error('Activity scope changed while loading. Refresh to reload.');
    if (pageIndex && JSON.stringify(capabilities) !== JSON.stringify(page.capabilities)) throw new Error('Activity access changed while loading. Refresh to reload.');
    total = page.total; capabilities = page.capabilities;
    for (const activity of page.activities) {
      if (!activity || typeof activity.id !== 'string' || ids.has(activity.id)) throw new Error('Activities changed while loading. Refresh to reload.');
      ids.add(activity.id); activities.push(activity);
    }
    if (!page.nextCursor) {
      if (activities.length !== total) throw new Error('Activity snapshot is incomplete. Refresh to reload.');
      return { activities, capabilities };
    }
    if (!page.activities.length || cursors.has(page.nextCursor)) throw new Error('Activity pagination did not advance');
    cursors.add(page.nextCursor); cursor = page.nextCursor;
  }
  throw new Error('Activity snapshot exceeds the supported page limit');
}
